import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { ensureTaxRepositoryConfigured } from "@/lib/taxRepository";
import { PageHeader } from "@/pages/layoutChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2,
  Copy,
  FileSpreadsheet,
  FileText,
  FileUp,
  HelpCircle,
  Loader2,
  Search,
  XCircle,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  downloadLaboralReport,
  formatFechaValidacionLocal,
  laboralExportErrorMessage,
  MSG_SIN_DATOS_EXPORTAR,
} from "@/lib/laboralReportExport";
import { toast } from "sonner";
import LaboralFlujoOperativo from "@/components/laboral/LaboralFlujoOperativo";
import PortalExternoGuiaPanel from "@/pages/laboral/PortalExternoGuiaPanel";
import { SAT_VALIDADOR_RFC_PORTAL_URL } from "@/lib/satPortalUrls";

const SAT_PORTAL_URL = SAT_VALIDADOR_RFC_PORTAL_URL;

const INSTRUCCIONES_DEFAULT =
  "Para un registro: Use la sección Individual, copie los datos con los botones rápidos y péguelos en la sección «Validación de RFC uno a uno» del SAT.\n" +
  "Para varios registros: Use la sección Masiva, genere el archivo .txt, cargúelo en la opción «Validación masiva de RFCs» del SAT resolviendo el captcha, y posteriormente cargue aquí el archivo de respuesta que le dé la autoridad.";

function estatusBadge(status) {
  const s = (status || "pendiente").toLowerCase();
  if (s === "valido") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (s === "invalido") return "bg-red-100 text-red-800 border-red-200";
  return "bg-amber-50 text-amber-900 border-amber-200";
}

function estatusLabel(status) {
  const s = (status || "pendiente").toLowerCase();
  if (s === "valido") return "Válido";
  if (s === "invalido") return "Inválido";
  return "Pendiente";
}

async function copyText(value, label) {
  if (!value) {
    toast.error(`No hay ${label} para copiar`);
    return;
  }
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copiado al portapapeles`);
  } catch {
    toast.error(`No se pudo copiar ${label}`);
  }
}

/**
 * Validación de contribuyentes RFC/CURP — split screen SAT + panel operativo.
 */
export default function ValidacionContribuyentesRfcCurp({ embedMode } = {}) {
  const isTramitesEmbed = embedMode === "tramites";
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [config, setConfig] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lastLayoutPath, setLastLayoutPath] = useState("");
  const [loteResultados, setLoteResultados] = useState(null);
  const [respuestaFile, setRespuestaFile] = useState(null);

  const cli = useMemo(() => clients.find((c) => c.id === clientId), [clients, clientId]);
  const selected = useMemo(
    () => workers.find((w) => w.id === selectedId) || null,
    [workers, selectedId],
  );
  const portalUrl = config?.sat_portal_url || SAT_PORTAL_URL;
  const instrucciones = config?.instrucciones || INSTRUCCIONES_DEFAULT;

  const workersByRfc = useMemo(() => {
    const map = new Map();
    workers.forEach((w) => {
      const key = (w.rfc || "").trim().toUpperCase();
      if (key) map.set(key, w);
    });
    return map;
  }, [workers]);

  const filasExportacionSat = useMemo(() => {
    if (!loteResultados?.items?.length) return [];
    return loteResultados.items.map((row) => {
      const w = workersByRfc.get((row.rfc || "").trim().toUpperCase());
      return {
        consecutivo: row.consecutivo,
        nombre_completo: row.nombre_completo || w?.nombre_completo || "",
        rfc: row.rfc || "",
        curp: row.curp || w?.curp || "",
        codigo_postal: row.codigo_postal || w?.codigo_postal || "",
        estatus: row.estatus || "",
        observaciones: row.resultado_raw || "",
        resultado_raw: row.resultado_raw || "",
      };
    });
  }, [loteResultados, workersByRfc]);

  const onExportarSat = async (formato) => {
    if (!clientId) {
      toast.error("Seleccione un cliente");
      return;
    }
    if (!filasExportacionSat.length) {
      toast.warning(MSG_SIN_DATOS_EXPORTAR);
      return;
    }
    setExporting(true);
    try {
      const ext = formato === "pdf" ? "pdf" : "xlsx";
      const filename = await downloadLaboralReport({
        endpoint: "/laboral/validacion-contribuyentes/exportar-reporte",
        payload: {
          client_id: clientId,
          formato,
          fecha_validacion: formatFechaValidacionLocal(),
          items: filasExportacionSat,
        },
        fallbackFilename: `reporte_validacion_sat.${ext}`,
      });
      toast.success(`Reporte descargado: ${filename}`);
    } catch (e) {
      toast.error(await laboralExportErrorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const loadWorkers = useCallback(async () => {
    if (!clientId) {
      setWorkers([]);
      return;
    }
    try {
      const { data } = await api.get("/laboral/trabajadores-registro", {
        params: { client_id: clientId, q: searchQ.trim() || undefined },
      });
      setWorkers(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setWorkers([]);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar trabajadores");
    }
  }, [clientId, searchQ]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, []);

  useEffect(() => {
    if (!clientId) {
      setConfig(null);
      setSelectedId("");
      return;
    }
    api
      .get("/laboral/validacion-contribuyentes/config", { params: { client_id: clientId } })
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig(null));
  }, [clientId]);

  useEffect(() => {
    const t = setTimeout(() => loadWorkers(), searchQ ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadWorkers, searchQ]);

  useEffect(() => {
    setSelectedId("");
    setLastLayoutPath("");
    setLoteResultados(null);
    setRespuestaFile(null);
  }, [clientId]);

  const onMarcarEstatus = async (estatus) => {
    if (!clientId || !selected?.id) return;
    setBusy(true);
    try {
      const { data } = await api.put(
        `/laboral/trabajadores-registro/${selected.id}/estatus-validacion`,
        { estatus },
        { params: { client_id: clientId } },
      );
      toast.success(`Trabajador marcado como ${estatus === "valido" ? "VÁLIDO" : "INVÁLIDO"}`);
      setWorkers((prev) => prev.map((w) => (w.id === data.id ? data : w)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo actualizar estatus");
    } finally {
      setBusy(false);
    }
  };

  const onGenerarLayout = async () => {
    if (!clientId) {
      toast.error("Seleccione un cliente");
      return;
    }
    setBusy(true);
    try {
      await ensureTaxRepositoryConfigured({
        clientId,
        dateStr: new Date().toISOString().slice(0, 10),
        direccion: "emitido",
      });
      const body = new FormData();
      body.append("client_id", clientId);
      const { data } = await api.post("/laboral/validacion-contribuyentes/generar-layout", body);
      const path = data.file_path || "";
      if (path) {
        try {
          await navigator.clipboard.writeText(path);
          toast.success("Ruta del layout copiada al portapapeles");
        } catch {
          toast.info("Layout generado; copie la ruta manualmente del recuadro inferior.");
        }
      }
      setLastLayoutPath(path);
      toast.success(data.message || `Layout generado (${data.line_count} RFC)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo generar el layout");
    } finally {
      setBusy(false);
    }
  };

  const onProcesarRespuesta = async () => {
    if (!clientId || !respuestaFile) {
      toast.error("Seleccione el archivo .txt de respuesta del SAT");
      return;
    }
    setBusy(true);
    try {
      const body = new FormData();
      body.append("client_id", clientId);
      body.append("archivo_respuesta", respuestaFile);
      const { data } = await api.post("/laboral/validacion-contribuyentes/procesar-respuesta", body);
      setLoteResultados(data);
      toast.success(data.message || "Respuesta procesada");
      await loadWorkers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo procesar el archivo");
    } finally {
      setBusy(false);
    }
  };

  const panelBody = (
      <div
        className="flex-1 grid grid-cols-1 xl:grid-cols-2 min-h-0 border-t border-slate-200"
        data-testid="validacion-contribuyentes-split"
      >
        {/* Panel izquierdo — operación */}
        <div className="overflow-y-auto p-4 md:p-6 space-y-4 bg-[#F4F6F8] border-b xl:border-b-0 xl:border-r border-slate-200">
          <div className="rounded-md border border-sky-200 bg-sky-50/90 px-3 py-3 text-xs text-slate-800 leading-relaxed flex gap-2">
            <HelpCircle className="w-4 h-4 shrink-0 text-sky-700 mt-0.5" />
            <p className="m-0 whitespace-pre-line">{instrucciones}</p>
          </div>

          <div className="bg-white rounded-md border border-slate-200 p-4 space-y-3">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Cliente (patrón)</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger data-testid="validacion-client-select" className="h-10">
                <SelectValue placeholder="Seleccione cliente" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.razon_social} — {c.rfc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {cli?.rfc ? (
              <p className="text-[11px] text-slate-500 m-0">RFC patrón: <strong>{cli.rfc}</strong></p>
            ) : null}
          </div>

          <Tabs defaultValue="individual" className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-9">
              <TabsTrigger value="individual" className="text-xs">A) Validación Individual</TabsTrigger>
              <TabsTrigger value="masiva" className="text-xs">B) Validación Masiva (Lote)</TabsTrigger>
            </TabsList>

            <TabsContent value="individual" className="mt-3 space-y-3">
              <div className="bg-white rounded-md border border-slate-200 p-4 space-y-3">
                <Label className="text-xs font-semibold text-[#0F2D5C]">Búsqueda de Datos de Contribuyentes (Personas Físicas y Morales)</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    placeholder="RFC, CURP o nombre…"
                    className="pl-8 h-9 text-xs"
                    disabled={!clientId}
                  />
                </div>
                <Select value={selectedId} onValueChange={setSelectedId} disabled={!clientId || !workers.length}>
                  <SelectTrigger className="h-10 text-xs">
                    <SelectValue placeholder={workers.length ? "Seleccione trabajador" : "Sin registros en MongoDB"} />
                  </SelectTrigger>
                  <SelectContent>
                    {workers.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        {(w.nombre_completo || w.curp || w.rfc || w.id).slice(0, 60)}
                        {" — "}
                        <span className="font-mono">{w.rfc || w.curp || "—"}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selected ? (
                  <div className="space-y-3 pt-2 border-t border-slate-100">
                    <div>
                      <Label className="text-[10px] uppercase text-slate-500">RFC</Label>
                      <div className="flex gap-2 mt-1">
                        <Textarea readOnly value={selected.rfc || "—"} className="font-mono text-sm min-h-[52px] resize-none" />
                        <Button type="button" variant="outline" size="sm" className="shrink-0 h-auto" onClick={() => copyText(selected.rfc, "RFC")}>
                          <Copy className="w-4 h-4 mr-1" /> Copiar RFC
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-slate-500">CURP</Label>
                      <Textarea readOnly value={selected.curp || "—"} className="font-mono text-sm min-h-[52px] resize-none" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-slate-500">Nombre completo</Label>
                      <Textarea readOnly value={selected.nombre_completo || "—"} className="text-sm min-h-[52px] resize-none" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-slate-500">Código postal</Label>
                      <div className="flex gap-2 mt-1">
                        <Textarea readOnly value={selected.codigo_postal || "—"} className="font-mono text-sm min-h-[44px] resize-none" />
                        <Button type="button" variant="outline" size="sm" className="shrink-0 h-auto" onClick={() => copyText(selected.codigo_postal, "CP")}>
                          <Copy className="w-4 h-4 mr-1" /> Copiar CP
                        </Button>
                      </div>
                    </div>
                    <p className={`text-xs inline-flex px-2 py-1 rounded border ${estatusBadge(selected.validacion_rfc_status)}`}>
                      Estatus: {estatusLabel(selected.validacion_rfc_status)}
                    </p>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        type="button"
                        disabled={busy}
                        className="bg-emerald-700 hover:bg-emerald-800"
                        onClick={() => onMarcarEstatus("valido")}
                        data-testid="btn-marcar-valido"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Marcar como VÁLIDO
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        disabled={busy}
                        onClick={() => onMarcarEstatus("invalido")}
                        data-testid="btn-marcar-invalido"
                      >
                        <XCircle className="w-4 h-4 mr-1" /> Marcar como INVÁLIDO
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 pt-2">
                    Seleccione un trabajador de la colección <code>trabajadores_registro</code> para validar uno a uno en el SAT.
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="masiva" className="mt-3 space-y-3">
              <div className="bg-white rounded-md border border-slate-200 p-4 space-y-4">
                <Button
                  type="button"
                  disabled={busy || !clientId}
                  onClick={onGenerarLayout}
                  className="w-full h-12 justify-start font-bold bg-[#0F2D5C] hover:bg-[#0a2247]"
                  data-testid="btn-generar-layout-validacion"
                >
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (
                    <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-white/20 text-xs mr-2">1</span>
                  )}
                  Generar Layout de Validación (.txt)
                </Button>
                {lastLayoutPath ? (
                  <p className="text-[11px] font-mono break-all bg-slate-50 border rounded p-2 m-0">{lastLayoutPath}</p>
                ) : null}

                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <Label className="text-xs font-semibold">2. Procesar Respuesta del SAT (.txt)</Label>
                  <Input
                    type="file"
                    accept=".txt"
                    disabled={!clientId || busy}
                    onChange={(e) => setRespuestaFile(e.target.files?.[0] || null)}
                    className="text-xs"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy || !clientId || !respuestaFile}
                    onClick={onProcesarRespuesta}
                    className="w-full h-11 justify-start font-semibold border-emerald-700 text-emerald-800"
                    data-testid="btn-procesar-respuesta-sat"
                  >
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (
                      <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-emerald-100 text-xs mr-2">2</span>
                    )}
                    <FileUp className="w-4 h-4 mr-2" />
                    Procesar Respuesta del SAT (.txt)
                  </Button>
                </div>
              </div>

              {loteResultados?.items?.length ? (
                <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b text-xs font-semibold text-[#0F2D5C]">
                    Resultado del lote — {loteResultados.valid_count} válidos / {loteResultados.invalid_count} inválidos
                  </div>
                  <div className="overflow-x-auto max-h-64">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>RFC</TableHead>
                          <TableHead>Nombre</TableHead>
                          <TableHead>Resultado SAT</TableHead>
                          <TableHead>Estatus</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loteResultados.items.map((row) => (
                          <TableRow
                            key={`${row.rfc}-${row.consecutivo}`}
                            className={row.estatus === "valido" ? "bg-emerald-50/80" : row.estatus === "invalido" ? "bg-red-50/80" : ""}
                          >
                            <TableCell className="font-mono text-xs">{row.rfc}</TableCell>
                            <TableCell className="text-xs max-w-[120px] truncate">{row.nombre_completo || "—"}</TableCell>
                            <TableCell className="text-[10px] max-w-[140px] truncate">{row.resultado_raw || "—"}</TableCell>
                            <TableCell>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${estatusBadge(row.estatus)}`}>
                                {estatusLabel(row.estatus)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="px-3 py-2 border-t border-slate-100 bg-white flex justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={exporting || !clientId}
                          className="text-xs"
                          data-testid="btn-exportar-resultados-sat"
                        >
                          {exporting ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <span className="mr-2" aria-hidden>📄</span>
                          )}
                          Exportar Resultados del SAT
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="text-xs">
                        <DropdownMenuItem
                          disabled={exporting}
                          onClick={() => onExportarSat("xlsx")}
                          data-testid="export-sat-excel"
                        >
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                          Exportar a Excel
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={exporting}
                          onClick={() => onExportarSat("pdf")}
                          data-testid="export-sat-pdf"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Exportar a PDF
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ) : null}
            </TabsContent>
          </Tabs>
        </div>

        {/* Panel derecho — guía portal SAT (sin iframe; el SAT bloquea embebido) */}
        <PortalExternoGuiaPanel
          title="Portal SAT — Validación de RFC"
          portalUrl={portalUrl}
          badge="Consulta pública SAT"
          intro="No requiere FIEL ni contraseña del cliente. Valide RFC o CURP de los trabajadores con captcha en el sitio oficial."
          steps={[
            {
              key: "abrir",
              title: "Abrir el validador SAT",
              description: "Use el botón verde para abrir el validador oficial del SAT (portalsat.plataforma.sat.gob.mx) en una pestaña nueva.",
            },
            {
              key: "individual",
              title: "Validación individual",
              description: "Copie RFC (y CP si aplica) desde el panel izquierdo y péguelos en «Validación de RFC uno a uno».",
            },
            {
              key: "masiva",
              title: "Validación masiva",
              description: "Genere el .txt aquí, súbalo en «Validación masiva de RFCs», resuelva captcha y descargue la respuesta.",
            },
            {
              key: "volver",
              title: "Registrar resultado en Visor Fiscal",
              description: "Marque Válido/Inválido (individual) o procese el .txt de respuesta (masiva) en el panel izquierdo.",
            },
          ]}
          openButtonLabel="Abrir portal SAT — Validación RFC"
          testId="btn-abrir-portal-sat-rfc"
          footerNote="Si usa el visor del IDE, prefiera «Abrir en el navegador» en Puertos; la vista previa integrada suele bloquear cookies del SAT."
        />
      </div>
  );

  return (
    <div className={isTramitesEmbed ? "" : "min-h-screen flex flex-col"}>
      {!isTramitesEmbed ? (
        <PageHeader
          title="Validación de Contribuyentes por RFC o CURP"
          subtitle="Consulte el portal SAT y registre resultados individual o por lote."
          breadcrumb={["Asistente Laboral y REPSE", "Validación de Contribuyentes"]}
        />
      ) : null}
      {isTramitesEmbed ? panelBody : <LaboralFlujoOperativo>{panelBody}</LaboralFlujoOperativo>}
    </div>
  );
}
